import React from 'react';
import { BodyText, FlexColumn, Input, Textarea } from '@make-software/cspr-design';
import Select, { components } from 'react-select';
import { TradeType } from 'app/console-api';
import { getTradeTypeConfig, isFeatureEnabled } from './trade-type-config';
import { ExtendedOffersResponse } from 'app/console-api/offers/offers-extended';
import { ErrorTextContainer, ErrorText, DropdownStyles } from 'app/components/common/common-styles';
import { RewardsValue, RewardsType } from 'app/console-api/apps/types';
import { RewardValueControl } from 'app/components/management/common/rewards/rewards-value-control';
import { AppCategoriesSelect } from 'app/components/management/common/app-categories-select/app-categories-select';
import { PaginationResponseType } from 'app/console-api';
import { AppCategory } from 'app/console-api/apps/categories';

const { Option, SingleValue } = components;

const StyledInput = Input;
const SubtitleBodyText = BodyText;

// Dropdown custom components
const DropdownOption = (props: any) => {
  return (
    <Option {...props}>
      <FlexColumn gap={4}>
        <BodyText size={2}>{props.data.label}</BodyText>
        {props.data.description && (
          <BodyText size={3} scale={'xs'} variation={'darkGray'}>
            {props.data.description}
          </BodyText>
        )}
      </FlexColumn>
    </Option>
  );
};

const SelectedDropdownValue = (props: any) => (
  <SingleValue {...props}>
    <FlexColumn gap={4}>
      <BodyText size={2}>{props.data.label}</BodyText>
    </FlexColumn>
  </SingleValue>
);

export interface DropdownValue {
  label: string;
  value: string;
  description?: string;
}

export interface DealFormFieldsProps {
  tradeType: TradeType;
  formData: {
    myOffer?: DropdownValue;
    title: string;
    customMetadata?: string;
    rewards: RewardsValue[];
    categories: number[];
  };
  errors: any;
  offerData: ExtendedOffersResponse[];
  rewardsTypes: RewardsType[];
  appCategories: PaginationResponseType<AppCategory> | null;
  onFieldChange: <K extends string>(field: K, value: any) => void;
  isEditing?: boolean;
}

/**
 * My Offer Field Component
 * Only rendered for trade types that require it (exchange, buy)
 */
export const MyOfferField: React.FC<DealFormFieldsProps> = ({
  tradeType,
  formData,
  errors,
  offerData,
  onFieldChange,
  isEditing,
}) => {
  if (!isFeatureEnabled(tradeType, 'requiresMyOffer')) {
    return null;
  }

  if (isEditing) {
    return null
  }

  const config = getTradeTypeConfig(tradeType);

  return (
    <FlexColumn gap={2}>
      <BodyText size={1}>{config.myOfferLabel}</BodyText>
      <SubtitleBodyText size={2} scale={'xs'} variation={'darkGray'}>
        {config.myOfferDescription}
      </SubtitleBodyText>

      <Select
        value={formData.myOffer}
        onChange={(e) => onFieldChange('myOffer', e as unknown as DropdownValue)}
        options={offerData.map((offer) => ({
          label: offer.offer.name,
          value: offer.offer.id,
          description: offer.offer.description
        }))}
        components={{ Option: DropdownOption, SingleValue: SelectedDropdownValue }}
        styles={DropdownStyles()}
      />
      {errors?.myOffer?.value?.message && (
        <ErrorTextContainer justify={'flex-start'} top={'46px'}>
          <ErrorText size={3}>{errors?.myOffer?.value?.message}</ErrorText>
        </ErrorTextContainer>
      )}
    </FlexColumn>
  );
};

/**
 * Title Field Component
 * Always shown for all trade types
 */
export const TitleField: React.FC<Pick<DealFormFieldsProps, 'formData' | 'errors' | 'onFieldChange'>> = ({
  formData,
  errors,
  onFieldChange
}) => {
  return (
    <>
      <StyledInput
        required={true}
        label={<BodyText size={1}>Title</BodyText>}
        placeholder={'Title'}
        value={formData.title}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onFieldChange('title', e.target.value);
        }}
      />
      {errors?.title?.message && (
        <ErrorTextContainer justify={'flex-start'}>
          <ErrorText size={3}>{errors?.title?.message}</ErrorText>
        </ErrorTextContainer>
      )}
    </>
  );
};

/**
 * Custom Metadata Field Component
 * Only shown for trade types that support it (exchange, sell, internal)
 */
export const CustomMetadataField: React.FC<Pick<DealFormFieldsProps, 'tradeType' | 'formData' | 'errors' | 'onFieldChange'>> = ({
  tradeType,
  formData,
  errors,
  onFieldChange
}) => {
  if (!isFeatureEnabled(tradeType, 'showCustomMetadata')) {
    return null;
  }

  return (
    <>
      <Textarea
        label={<BodyText size={1}>Custom metadata</BodyText>}
        placeholder={'Custom JSON'}
        value={formData.customMetadata?.toString() || undefined}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onFieldChange('customMetadata', e.target.value);
        }}
      />
      {errors?.customMetadata?.message && (
        <ErrorTextContainer justify={'flex-start'}>
          <ErrorText size={3}>{errors?.customMetadata?.message}</ErrorText>
        </ErrorTextContainer>
      )}
    </>
  );
};

/**
 * Categories Field Component
 * Only shown for trade types that support it (exchange, sell, internal)
 */
export const CategoriesField: React.FC<Pick<DealFormFieldsProps, 'tradeType' | 'formData' | 'errors' | 'appCategories' | 'onFieldChange'>> = ({
  tradeType,
  formData,
  errors,
  appCategories,
  onFieldChange
}) => {
  if (!isFeatureEnabled(tradeType, 'showCategories')) {
    return null;
  }

  return (
    <FlexColumn align={'flex-start'} gap={8}>
      <BodyText size={1} scale={'sm'}>
        Task Categories
      </BodyText>
      <div style={{ width: '100%' }}>
        <AppCategoriesSelect
          categories={appCategories?.data || []}
          selectedIds={formData.categories}
          handleChange={(ids: number[]) => {
            onFieldChange('categories', ids);
          }}
        />
      </div>
      {errors?.categories?.message && (
        <ErrorTextContainer justify={'flex-start'}>
          <ErrorText size={3}>{errors?.categories?.message}</ErrorText>
        </ErrorTextContainer>
      )}
    </FlexColumn>
  );
};

/**
 * Rewards Field Component
 * Only shown for trade types that support it (exchange, sell, internal)
 */
export const RewardsField: React.FC<Pick<DealFormFieldsProps, 'tradeType' | 'formData' | 'errors' | 'rewardsTypes' | 'onFieldChange'>> = ({
  tradeType,
  formData,
  errors,
  rewardsTypes,
  onFieldChange
}) => {
  if (!isFeatureEnabled(tradeType, 'showRewards')) {
    return null;
  }

  return (
    <FlexColumn gap={8}>
      <RewardValueControl
        rewardValues={formData.rewards}
        rewardTypes={rewardsTypes}
        onChange={(updatedRewards: RewardsValue[]) => {
          onFieldChange('rewards', updatedRewards);
        }}
      />
      {errors?.rewards?.message && (
        <ErrorTextContainer justify={'flex-start'} top={'12px'}>
          <ErrorText size={3}>{errors?.rewards?.message}</ErrorText>
        </ErrorTextContainer>
      )}
    </FlexColumn>
  );
};

/**
 * Complete Deal Form Fields
 * Renders all applicable fields based on trade type configuration
 */
export const DealFormFields: React.FC<DealFormFieldsProps> = (props) => {
  return (
    <FlexColumn gap={24}>
      <MyOfferField {...props} />
      <TitleField
        formData={props.formData}
        errors={props.errors}
        onFieldChange={props.onFieldChange}
      />
      <CustomMetadataField
        tradeType={props.tradeType}
        formData={props.formData}
        errors={props.errors}
        onFieldChange={props.onFieldChange}
      />
      <CategoriesField
        tradeType={props.tradeType}
        formData={props.formData}
        errors={props.errors}
        appCategories={props.appCategories}
        onFieldChange={props.onFieldChange}
      />
      <RewardsField
        tradeType={props.tradeType}
        formData={props.formData}
        errors={props.errors}
        rewardsTypes={props.rewardsTypes}
        onFieldChange={props.onFieldChange}
      />
    </FlexColumn>
  );
};
